import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ABC_AUTH_API } from 'app/core/auth-abc/auth-abc-api.interceptor';
import { UserLevelSettingsResponseData } from 'app/core/interfaces/pricing.interface';
import { ApiResponseModel } from 'app/core/models/apiResponse.model';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';

const httpOptions = {
    context: new HttpContext().set(ABC_AUTH_API, true),
};

export interface BalanceInfo {
    totalBalance: number;
    totalCredits: number;
    totalDebits: number;
    transactionCount: number;
    currencyCode: string;
    currencySymbol: string;
}

export interface LevelInfo {
    number: number;
    name: string;
}

export interface LevelDetails {
    currentLevel: LevelInfo;
    nextLevel: LevelInfo;
}

export interface WalletBalanceResponse {
    walletBalance: BalanceInfo;
    reelBalance: BalanceInfo;
    level: LevelDetails;
}

@Injectable({
    providedIn: 'root',
})
export class FilmMakerWalletService {
    private walletBalanceSubject = new BehaviorSubject<BalanceInfo | null>(
        null
    );
    private reelBalanceSubject = new BehaviorSubject<BalanceInfo | null>(null);
    private levelSubject = new BehaviorSubject<LevelDetails | null>(null);

    // Expose the BehaviorSubjects as Observables for readonly usage
    get walletBalance$(): Observable<BalanceInfo | null> {
        return this.walletBalanceSubject.asObservable();
    }

    get reelBalance$(): Observable<BalanceInfo | null> {
        return this.reelBalanceSubject.asObservable();
    }

    get level$(): Observable<LevelDetails | null> {
        return this.levelSubject.asObservable();
    }

    // Getters for current values
    get walletBalance(): BalanceInfo | null {
        return this.walletBalanceSubject.value;
    }

    get reelBalance(): BalanceInfo | null {
        return this.reelBalanceSubject.value;
    }

    get level(): LevelDetails | null {
        return this.levelSubject.value;
    }

    // Setters
    setWalletBalance(value: BalanceInfo | null): void {
        this.walletBalanceSubject.next(value);
    }

    setReelBalance(value: BalanceInfo | null): void {
        this.reelBalanceSubject.next(value);
    }

    setLevel(value: LevelDetails | null): void {
        this.levelSubject.next(value);
    }

    // Method to update all wallet data at once
    updateWalletData(data: WalletBalanceResponse): void {
        this.setWalletBalance(data.walletBalance);
        this.setReelBalance(data.reelBalance);
        this.setLevel(data.level);
    }

    // Method to clear all wallet data
    clearWalletData(): void {
        this.setWalletBalance(null);
        this.setReelBalance(null);
        this.setLevel(null);
    }

    // Method to get current wallet data as an object
    getCurrentWalletData(): {
        walletBalance: BalanceInfo | null;
        reelBalance: BalanceInfo | null;
        level: LevelDetails | null;
    } {
        return {
            walletBalance: this.walletBalance,
            reelBalance: this.reelBalance,
            level: this.level,
        };
    }

    // Combined observable for all wallet data
    get allWalletData$(): Observable<{
        walletBalance: BalanceInfo | null;
        reelBalance: BalanceInfo | null;
        level: LevelDetails | null;
    }> {
        return combineLatest([
            this.walletBalance$,
            this.reelBalance$,
            this.level$,
        ]).pipe(
            map(([walletBalance, reelBalance, level]) => ({
                walletBalance,
                reelBalance,
                level,
            }))
        );
    }

    constructor(private readonly http: HttpClient) {}

    /**
     * Get filmmaker wallet balance
     * @returns Observable of wallet balance response
     */
    getWalletBalance(): Observable<ApiResponseModel<WalletBalanceResponse>> {
        return this.http.get<ApiResponseModel<WalletBalanceResponse>>(
            '/wallet/filmMaker/wallet-balance',
            httpOptions
        );
    }

    getFilmMakerLevelSettings(): Observable<
        ApiResponseModel<UserLevelSettingsResponseData>
    > {
        return this.http.get<ApiResponseModel<UserLevelSettingsResponseData>>(
            '/wallet/filmMaker/level',
            httpOptions
        );
    }
}
